import { SITE_ORIGIN } from "./siteSeo";

export const PLAY_BOARD_PREVIEW_BASE = (
    process.env.REACT_APP_PREVIEW_IMAGE_BASE ||
    "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/preview"
).replace(/\/$/, "");

export const PLAY_BOARD_UNAVAILABLE_IMAGE = `${PLAY_BOARD_PREVIEW_BASE}/unavailable.png`;

export const DEFAULT_PLAY_BOARD_DESCRIPTION = "Check out this amazing puzzle!";

export const JIGSAW_PLAY_BOARD_PATH_RE =
    /^\/puzzle\/play-jigsaw\/([^/]+)\/board\/([^/]+)\/?$/;
export const SLIDING_PLAY_BOARD_PATH_RE =
    /^\/puzzle\/play-sliding\/([^/]+)\/board\/([^/]+)\/?$/;

/** Board play URLs indexed per backend robots + play-url SEO spec. */
export function isIndexablePlayBoardPath(pathname) {
    return (
        JIGSAW_PLAY_BOARD_PATH_RE.test(pathname) ||
        SLIDING_PLAY_BOARD_PATH_RE.test(pathname)
    );
}

function pickNonEmptyString(...values) {
    for (const v of values) {
        if (typeof v === "string" && v.trim()) return v.trim();
    }
    return "";
}

/** Align with ShareService: first non-blank story title, else puzzle type label. */
export function pickStoryTitleForPlaySeo(story, fallbackLabel) {
    if (!story || typeof story !== "object") return fallbackLabel;
    return (
        pickNonEmptyString(
            story.english?.title,
            story.en?.title,
            story.french?.title,
            story.fr?.title,
            story.stories?.en?.title,
            story.stories?.fr?.title,
            story.storyJson?.stories?.en?.title,
            story.storyJson?.stories?.fr?.title,
        ) || fallbackLabel
    );
}

export function buildJigsawPlayBoardUrl(storyId, jigsawId) {
    return `${SITE_ORIGIN}/puzzle/play-jigsaw/${encodeURIComponent(storyId)}/board/${encodeURIComponent(jigsawId)}`;
}

export function buildSlidingPlayBoardUrl(storyId, slidingId) {
    return `${SITE_ORIGIN}/puzzle/play-sliding/${encodeURIComponent(storyId)}/board/${encodeURIComponent(slidingId)}`;
}

export function resolveJigsawPlayBoardOgImage({ story, board, storyId, jigsawId }) {
    const fromBoard = pickNonEmptyString(
        board?.previewImageUrl,
        board?.preview_image_url,
        board?.imageUrl,
        board?.image_url,
    );
    if (fromBoard) return fromBoard;

    const fromStory = pickNonEmptyString(
        story?.imagePreviewUrl,
        story?.image_preview_url,
        story?.imageUrl,
        story?.image_url,
    );
    if (fromStory) return fromStory;

    if (storyId && jigsawId) {
        return `${PLAY_BOARD_PREVIEW_BASE}/jigsaw/${storyId}/${jigsawId}.png`;
    }
    return PLAY_BOARD_UNAVAILABLE_IMAGE;
}

export function resolveSlidingPlayBoardOgImage({ story, board, storyId, slidingId }) {
    const fromBoard = pickNonEmptyString(
        board?.previewImageUrl,
        board?.preview_image_url,
        board?.imageUrl,
        board?.image_url,
    );
    if (fromBoard) return fromBoard;

    const fromStory = pickNonEmptyString(
        story?.imagePreviewUrl,
        story?.image_preview_url,
        story?.imageUrl,
        story?.image_url,
    );
    if (fromStory) return fromStory;

    if (storyId && slidingId) {
        return `${PLAY_BOARD_PREVIEW_BASE}/sliding/${storyId}/${slidingId}.png`;
    }
    return PLAY_BOARD_UNAVAILABLE_IMAGE;
}

/**
 * @param {"jigsaw"|"sliding"} kind
 * @param {{ story?: object, board?: object, storyId: string, boardId: string, noindex?: boolean }} opts
 */
export function buildPlayBoardSeoMeta({ kind, story, board, storyId, boardId, noindex = false }) {
    const fallbackLabel = kind === "jigsaw" ? "Jigsaw" : "Sliding";
    const title = pickStoryTitleForPlaySeo(story, fallbackLabel);
    const playUrl =
        kind === "jigsaw"
            ? buildJigsawPlayBoardUrl(storyId, boardId)
            : buildSlidingPlayBoardUrl(storyId, boardId);
    const imageUrl =
        kind === "jigsaw"
            ? resolveJigsawPlayBoardOgImage({ story, board, storyId, jigsawId: boardId })
            : resolveSlidingPlayBoardOgImage({ story, board, storyId, slidingId: boardId });

    return {
        title,
        fullTitle: `${title} | Puzzle Interact`,
        description: DEFAULT_PLAY_BOARD_DESCRIPTION,
        playUrl,
        imageUrl,
        robots: noindex ? "noindex, follow" : "index, follow",
    };
}
